import React, { useEffect } from 'react';
import { ArrowRight, CheckCircle, Sparkles, ChevronDown } from 'lucide-react';
import { useBreakpoint } from '@/hooks/use-mobile';
import CTAButton from '../CTAButton';
import { Link } from 'react-router-dom';

const FeaturesHero = () => {
  const {
    isMobile
  } = useBreakpoint();
  
  const features = [
    {
      title: 'IntelliForms',
      description: 'AI-powered forms that adapt to user input, automate responses, and streamline data collection.',
      anchor: 'intelliforms-section',
      icon: <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
    },
    {
      title: 'AI Assistants',
      description: 'Custom AI chatbots trained on your data to handle FAQs, customer interactions, and automation.',
      anchor: 'ai-assistants-section',
      icon: <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
    },
    {
      title: 'Flow Builder',
      description: 'No-code drag-and-drop automation for creating structured AI-driven workflows.',
      anchor: 'flow-builder-section',
      icon: <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
    },
    {
      title: 'Spotlight Pages',
      description: 'AI-powered landing pages that bring a person, product, or brand to life. Combine rich media and links with a custom-trained chatbot to engage visitors.',
      anchor: 'spotlight-pages-section',
      icon: <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
    }
  ];
  
  const scrollToContent = () => {
    document.getElementById('features-content')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      console.log(`Element with ID "${sectionId}" not found`);
    }
  };
  
  return <section className="min-h-[90vh] bg-white relative overflow-hidden pt-20 md:pt-32">
      {/* Subtle Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
        <div className="absolute top-1/4 right-1/4 w-72 h-72 rounded-full bg-[#003a70]/30 blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full bg-[#FEE6D8]/50 blur-3xl"></div>
        <div className="absolute top-3/4 right-1/3 w-64 h-64 rounded-full bg-[#003a70]/20 blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 h-full flex flex-col justify-center items-center py-12 md:py-24 relative z-10">
        <div className="max-w-5xl mx-auto text-center mb-8 md:mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-accent text-[#003a70] text-xs md:text-sm font-medium mb-3 md:mb-5">
            <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>AI-Powered Solutions</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#003a70] leading-tight mb-4 md:mb-6 px-1 sm:px-2">
            {isMobile ? (
              <>
                <span className="block">Build Intelligent</span>
                <span className="block">AI Tools</span>
                <span className="block mt-2 text-[#003a70]/80">Without Code</span>
              </>
            ) : (
              <>
                <span className="block">Build Intelligent AI Tools</span>
                <span className="block mt-2 text-[#003a70]/80">Without Writing Code</span>
              </>
            )}
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6 md:mb-8 px-2 sm:px-4">
            {isMobile ? 
              "Create AI assistants, forms, and workflows in minutes, not months." :
              "AskSpot gives you everything you need to build AI-powered assistants, interactive forms, and automated workflows in minutes, not months."
            }
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <CTAButton 
              variant="primary" 
              className="group relative overflow-hidden font-semibold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg shadow-lg w-full sm:w-auto"
              to="/pricing"
            >
              <span className="relative z-10 flex items-center">
                Start Your Free Trial
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </CTAButton>
            
            <Link to="/book-demo">
              <CTAButton variant="secondary" className="group text-[#003a70] backdrop-blur-sm w-full sm:w-auto">
                <span className="flex items-center">
                  Schedule Demo
                  <ArrowRight className="ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </CTAButton>
            </Link>
          </div>
        </div>
        
        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 w-full max-w-6xl animate-fade-in-up" style={{
        animationDelay: '0.2s'
      }}>
          {features.map((feature, index) => (
            <div key={index} 
              className="bg-white shadow-lg p-4 sm:p-6 rounded-xl border border-gray-100 hover:shadow-xl transition-shadow group cursor-pointer"
              onClick={() => scrollToSection(feature.anchor)}
            >
              <div className="bg-[#FEE6D8] rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mb-3 sm:mb-4 text-[#003a70] group-hover:scale-110 transition-transform">
                {feature.icon}
              </div>
              <h3 className="font-semibold text-base sm:text-lg text-[#003a70] mb-1 sm:mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm mb-2">{feature.description}</p>
              <div className="mt-2 text-xs sm:text-sm font-medium text-[#003a70] flex items-center opacity-80 group-hover:opacity-100 transition-opacity">
                <span>Learn more</span>
                <ArrowRight className="ml-1 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </div>
          ))}
        </div>
        
        {/* Scroll Down Indicator - Moved further down */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer" onClick={scrollToContent}>
          <ChevronDown className="h-6 w-6 text-[#003a70]/70" />
        </div>
      </div>
    </section>;
};

export default FeaturesHero;
